// photo.js — pre-flight photo normalizer for upload flows.
//
// Goal: take whatever a user picked in their file dialog and turn it into a
// File object the rest of the pipeline can hand straight to <img> / canvas /
// Firebase Storage without surprise failures. Surfaces clear, actionable
// errors instead of letting the wizard hang on a format the browser can't
// decode.
//
// Used by /upload (PreUploadForm). Can be wired into /commission/upload too.

const NATIVE_IMAGE_TYPES = new Set([
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/bmp",
  "image/avif",
]);

// HEIC MIME types vary by browser. Some report image/heic, some heif,
// some leave it blank (in which case we have to sniff by extension).
const HEIC_MIME_TYPES = new Set([
  "image/heic",
  "image/heif",
  "image/heic-sequence",
  "image/heif-sequence",
]);

// Hard-reject categories. We could try to be cleverer (e.g. extract a frame
// from a video, or rasterize an SVG) but for a pancake reference photo flow
// the right answer is to say no and ask for a photo.
const REJECT_PATTERNS = [
  { test: (f) => f.type.startsWith("video/"),                      reason: "video", hint: "Please upload a still photo, not a video." },
  { test: (f) => f.type === "application/pdf",                     reason: "pdf",   hint: "PDFs aren't supported — please upload a JPEG, PNG, or HEIC photo." },
  { test: (f) => f.type === "image/svg+xml",                       reason: "svg",   hint: "SVGs aren't supported — please upload a regular photo (JPEG, PNG, or HEIC)." },
  { test: (f) => /\.(cr2|nef|arw|dng|raw|rw2|orf|raf|pef)$/i.test(f.name), reason: "raw",   hint: "RAW camera files aren't supported in the browser. Open the photo in Preview or Photos first and export as JPEG." },
  { test: (f) => /\.tiff?$/i.test(f.name) || f.type === "image/tiff", reason: "tiff", hint: "TIFF files aren't supported. Please convert to JPEG or PNG first." },
];

const MAX_BYTES = 10 * 1024 * 1024; // matches storage.rules cap

function isHeicByExt(file) {
  return /\.(heic|heif)$/i.test(file.name);
}

function isHeic(file) {
  return HEIC_MIME_TYPES.has(file.type) || isHeicByExt(file);
}

// Convert HEIC blob → JPEG blob via heic2any (dynamic import keeps the
// ~600KB WASM out of the main bundle for the 95% of users who never need it).
async function convertHeicToJpeg(file) {
  const { default: heic2any } = await import("heic2any");
  const blob = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.92 });
  // heic2any returns Blob | Blob[] depending on whether the source is a
  // sequence. We want a single frame — take the first if it gave us an array.
  const single = Array.isArray(blob) ? blob[0] : blob;
  const newName = file.name.replace(/\.(heic|heif)$/i, ".jpg");
  return new File([single], newName, { type: "image/jpeg", lastModified: Date.now() });
}

/**
 * Normalize a user-picked File for upload.
 * @param {File} file — what the file input gave us
 * @returns {Promise<{ file: File|null, error: string|null, converted?: boolean }>}
 */
export async function normalizePhotoForUpload(file) {
  if (!file) {
    return { file: null, error: "No file selected." };
  }

  // Reject obvious non-photo categories first — these have specific copy.
  for (const r of REJECT_PATTERNS) {
    if (r.test(file)) {
      return { file: null, error: r.hint };
    }
  }

  // HEIC conversion path. Reports "converted: true" so callers can show a
  // brief progress message ("Converting iPhone photo…") if they like.
  if (isHeic(file)) {
    try {
      const converted = await convertHeicToJpeg(file);
      if (converted.size > MAX_BYTES) {
        return { file: null, error: `That photo is ${(converted.size / 1024 / 1024).toFixed(1)} MB after conversion — too large. Try a smaller or compressed version.` };
      }
      return { file: converted, error: null, converted: true };
    } catch (err) {
      console.warn("HEIC conversion failed:", err);
      return { file: null, error: "Couldn't convert this HEIC photo. Try opening it in Preview and exporting as JPEG, then re-upload." };
    }
  }

  // Native-decodable images.
  if (NATIVE_IMAGE_TYPES.has(file.type) || (!file.type && /\.(jpe?g|png|webp|gif|bmp|avif)$/i.test(file.name))) {
    if (file.size > MAX_BYTES) {
      return { file: null, error: `That photo is ${(file.size / 1024 / 1024).toFixed(1)} MB — please use one under 10 MB (most phone photos are under 5 MB).` };
    }
    return { file, error: null };
  }

  // Catch-all for unknown types.
  return {
    file: null,
    error: `That file (${file.type || file.name.split(".").pop()?.toUpperCase() || "unknown type"}) isn't supported. Please upload a JPEG, PNG, or HEIC photo.`,
  };
}

// Accept= attribute string for <input type="file"> — narrows the OS picker
// so users see the same set of supported formats they'd be allowed to use.
// Note: the OS picker doesn't always honor `accept` strictly (especially
// macOS Finder for HEIC), so we still need the runtime normalizer.
export const PHOTO_ACCEPT_ATTR = [
  "image/jpeg", "image/jpg", "image/png", "image/webp", "image/gif", "image/bmp", "image/avif",
  "image/heic", "image/heif", "image/heic-sequence", "image/heif-sequence",
  ".jpg", ".jpeg", ".png", ".webp", ".gif", ".bmp", ".avif", ".heic", ".heif",
].join(",");
